import { Button, CircularProgress } from '@mui/material';
import { ButtonProps } from '@mui/material/Button';
import React from 'react';

import theme from '../../theme';

type ActionButtonType = 'primary' | 'dark' | 'outline' | 'secondary' | 'danger';

interface CPActionButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  appearance?: ActionButtonType;
  fullFlex?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

const CPActionButton: React.FC<CPActionButtonProps> = ({
  appearance = 'primary',
  fullFlex = false,
  loading = false,
  icon,
  disabled,
  children,
  sx,
  ...props
}) => {
  const getStyles = () => {
    switch (appearance) {
      case 'primary':
        return {
          variant: 'contained',
          sx: {
            backgroundColor: 'buttonPrimary.main',
            color: 'textDarkPrimary.primary',
            '&:disabled': {
              backgroundColor: theme.palette.border.main,
              color: theme.palette.textSecondary.primary,
            },
          },
        };

      case 'dark':
        return {
          variant: 'contained',
          sx: {
            backgroundColor: 'textDarkPrimary.primary',
            color: '#FFF',
          },
        };

      case 'outline':
        return {
          variant: 'outlined',
          sx: {
            border: `1px solid ${theme.palette.gray500.main}`,
            color: 'textDarkPrimary.primary',
            backgroundColor: '#FFF',
          },
        };

      case 'secondary':
        return {
          variant: 'contained',
          sx: {
            backgroundColor: theme.palette.border.light,
            color: 'textDarkPrimary.primary',
          },
        };

      case 'danger':
        return {
          variant: 'contained',
          sx: {
            backgroundColor: '#DC2626',
            color: '#FFF',
          },
        };

      default:
        return {};
    }
  };

  const { variant, sx: typeStyles } = getStyles();

  return (
    <Button
      {...props}
      variant={variant as any}
      startIcon={icon ?? props.startIcon}
      disabled={disabled || loading}
      sx={{
        textTransform: 'none',
        borderRadius: '4px',
        px: 4,
        py: 1.5,
        fontWeight: 600,
        height: '48px',
        boxShadow: 'none',
        ...(fullFlex && { flex: 1 }),
        '&:hover': { boxShadow: 'none' },
        ...typeStyles,
        ...sx,
      }}
    >
      {loading ? <CircularProgress size={18} color="inherit" /> : children}
    </Button>
  );
};

export default CPActionButton;
